import type { DiseaseTaxonomyNode, TreeNode } from "../types/diseaseTaxonomy.types";
import { diseaseNameMatchesSearchQuery } from "./diseaseDisplayName";

export function buildTree(flatNodes: DiseaseTaxonomyNode[]): TreeNode[] {
  const map = new Map<number, TreeNode>();
  const roots: TreeNode[] = [];

  for (const node of flatNodes) {
    const treeNode: TreeNode = { ...node, children: [] };
    map.set(node.id, treeNode);
  }

  for (const node of flatNodes) {
    const treeNode = map.get(node.id)!;
    if (node.parent_id != null) {
      const parent = map.get(node.parent_id);
      if (parent) parent.children.push(treeNode);
    } else {
      roots.push(treeNode);
    }
  }

  return roots;
}

export function getVisibleRows(
  roots: TreeNode[],
  expandedIds: Set<number>
): TreeNode[] {
  const result: TreeNode[] = [];

  const walk = (nodes: TreeNode[]) => {
    for (const node of nodes) {
      result.push(node);
      if (node.children.length > 0 && expandedIds.has(node.id)) {
        walk(node.children);
      }
    }
  };

  walk(roots);
  return result;
}

export function searchTree(
  query: string,
  flatNodes: DiseaseTaxonomyNode[]
): { matchIds: Set<number>; expandIds: Set<number> } {
  const q = query.toLowerCase().trim();
  if (!q) return { matchIds: new Set(), expandIds: new Set() };

  const parentMap = new Map<number, number | null>();
  for (const n of flatNodes) parentMap.set(n.id, n.parent_id);

  const matchIds = new Set<number>();
  const expandIds = new Set<number>();

  for (const n of flatNodes) {
    if (diseaseNameMatchesSearchQuery(n.name, q)) {
      matchIds.add(n.id);
      let pid = n.parent_id;
      while (pid != null && !expandIds.has(pid)) {
        expandIds.add(pid);
        pid = parentMap.get(pid) ?? null;
      }
    }
  }

  return { matchIds, expandIds };
}

export function getAllDescendantIds(node: TreeNode): number[] {
  const ids: number[] = [node.id];
  for (const child of node.children) {
    ids.push(...getAllDescendantIds(child));
  }
  return ids;
}

export function getAllNodeIds(roots: TreeNode[]): Set<number> {
  const ids = new Set<number>();
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      if (n.children.length > 0) ids.add(n.id);
      walk(n.children);
    }
  };
  walk(roots);
  return ids;
}
